import React, { Component } from "react";
import { Breadcrumb, Button, Grid, Icon, Placeholder, Modal, Form, TextArea } from "semantic-ui-react";
import { observable } from "mobx";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import TourStore from "~/stores/TourStore";
import TourStatus from "./TourStatus";
import "../styles/TourHeader.less";
import TourNavLabel from "../constants/TourNavLabel";
import TourClientFacingStatus from "~/constants/Tour/TourClientFacingStatus";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import PromiseButton from "~/components/element/PromiseButton";
import FormStore from "~/stores/FormStore";
import { getValidationErrors } from "~/utils/ReactReformed/ValidateSchema";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import TourUpgradeRequestStore from "~/stores/Tour/TourUpgradeRequestStore";
import TourIsActiveToggleButton from "~/applications/panel/components/TourIsActiveToggleButton";

@observer
class TourHeader extends Component {
  @observable formStore;

  constructor(props) {
    super(props);
    const { tourId, active, t } = props;

    this.onButtonClick = this.onButtonClick.bind(this);
    this.formStore = new FormStore({ upgradeTourMessage: "" });

    this.tourUpgradeRequestStore = new TourUpgradeRequestStore();

    this.sections = [
      { key: "tour", content: <Link to="/tours">{t("myTours")}</Link> },
      { key: "tourId", content: <Link to={`/tours/${tourId}`}>Tour #{tourId}</Link> },
      { key: active, content: t(TourNavLabel[active]), active: true },
    ];

    this.state = {
      previewAndOrder: false,
    };
  }

  onButtonClick() {
    if (this.isPhotosOnlyPublished()) {
      this.setState({ previewAndOrder: true });
    } else {
      this.viewTour();
    }
  }

  onModalClose() {
    this.setState({ previewAndOrder: false });
  }

  viewTour = () => {
    const { tourId } = this.props;
    window.open(`//${window.location.host}/${tourId}`);
  };

  isTourLoaded() {
    const { tourStore } = this.props;

    if (!tourStore) {
      return false;
    }

    const { tour } = tourStore;

    return tour.tourId !== null;
  }

  isPhotosOnlyPublished() {
    if (!this.isTourLoaded()) {
      return false;
    }

    const { tourStore } = this.props;
    const { tour } = tourStore;

    return tour.clientFacingStatus === TourClientFacingStatus.PHOTOS_READY;
  }

  requestTourUpgrade() {
    const { tourStore, showError } = this.props;
    const { tour } = tourStore;
    const { model } = this.formStore;

    this.setState({ previewAndOrder: false });

    return this.tourUpgradeRequestStore
      .create(tour.tourId, { message: model.upgradeTourMessage })
      .catch(e => showError(e));
  }

  render() {
    const { tourStore, t, customerHasUrbanimmersiveGroupSpecialPricing } = this.props;
    const { tour } = tourStore;
    const { tourUpgradeRequestSent, isSaving: isSendingTourUpgradeRequest } = this.tourUpgradeRequestStore;

    if (!tourStore) {
      return null;
    }

    const placeholder = (
      <Placeholder>
        <Placeholder.Header>
          <Placeholder.Line />
          <Placeholder.Line />
        </Placeholder.Header>
      </Placeholder>
    );

    const fullStreet = this.isTourLoaded() ? <h1>{tour.address.street}</h1> : placeholder;
    const tourStatus = this.isTourLoaded() ? <TourStatus tour={tour} /> : placeholder;
    const tourIsActiveToggleButton = this.isTourLoaded() ? (
      <TourIsActiveToggleButton
        tourStore={tourStore}
        customerHasUrbanimmersiveGroupSpecialPricing={customerHasUrbanimmersiveGroupSpecialPricing}
      />
    ) : (
      placeholder
    );
    const { previewAndOrder } = this.state;

    const { model, onChange, getAggregatedError, onBlur } = this.formStore;
    const validation = getValidationErrors({ upgradeTourMessage: { maxLength: 500 } }, model);
    const upgradeTourMessageError = getAggregatedError(
      "upgradeTourMessage",
      validation,
      {},
      { showErrorsOnBlur: false },
    );

    return (
      <>
        <Modal open={previewAndOrder} onClose={() => this.onModalClose()} size="tiny">
          <Modal.Header>{t("upgrade")}</Modal.Header>
          <Modal.Content>
            {tourUpgradeRequestSent && <strong>{t("clientPanelMisc:requestAlreadySent")}</strong>}
            <p>
              <a href={tour ? tour.brandedTourUrl : "#"} rel="noopener noreferrer" target="_blank">
                {t("clientPanelMisc:previewTour")}
              </a>
            </p>
            {!tourUpgradeRequestSent && <p>{t("clientPanelMisc:upgradePropertyPhotos")}</p>}
            {!tourUpgradeRequestSent && (
              <Form error>
                <Form.Field
                  label={t("clientPanelMisc:specialInstructions")}
                  control={TextArea}
                  name="upgradeTourMessage"
                  onBlur={onBlur}
                  value={model.upgradeTourMessage}
                  onChange={onChange}
                  error={upgradeTourMessageError}
                />
              </Form>
            )}
          </Modal.Content>
          <Modal.Actions>
            {!tourUpgradeRequestSent && (
              <PromiseButton
                primary
                onClick={() => this.requestTourUpgrade()}
                disabled={!this.isTourLoaded() || !validation.isValid}
              >
                {t("request")}
              </PromiseButton>
            )}
            <Button secondary onClick={() => this.onModalClose()}>
              {t("cancel")}
            </Button>
          </Modal.Actions>
        </Modal>
        <Grid.Row className="tour-header-breadcrumb-container">
          <Grid.Column width={16}>
            <Breadcrumb icon="right angle" sections={this.sections} />
          </Grid.Column>
        </Grid.Row>

        <div className="tour-header-main-container">
          <div className="tour-header-controls-container">
            <div className="tour-header-status-container">
              {tourStatus}
              {tourIsActiveToggleButton}
            </div>
            <Button
              className="tour-header-view-btn"
              disabled={!this.isTourLoaded()}
              onClick={this.onButtonClick}
              primary
            >
              {this.isPhotosOnlyPublished() ? t("clientPanelMisc:previewAndUpgrade") : t("clientPanelMisc:viewTour")}
              <Icon name="caret right" />
            </Button>
          </div>

          <div className="tour-header-address-container">{fullStreet}</div>
        </div>
        <LoadingWrapper loading={isSendingTourUpgradeRequest} />
      </>
    );
  }
}
TourHeader.propTypes = {
  active: PropTypes.string.isRequired,
  tourId: PropTypes.number,
  tourStore: PropTypes.instanceOf(TourStore).isRequired,
  t: PropTypes.func.isRequired,
  customerHasUrbanimmersiveGroupSpecialPricing: PropTypes.bool.isRequired,
};

const TourHeaderWithError = ErrorWrapper(TourHeader);

export default withTranslation()(TourHeaderWithError);
